// src/features/executive/shared/hooks/use-executive-query.ts
// Generic Redux-aware query hook for autonomous widgets.
// - Subscribes to executive.filters + executive.lastRefreshAt
// - Accepts optional scopedFilters for drill-down pre-filter overrides
// - Registers loading state under widgetKey in executive.loadingWidgets
//   for aggregate gating (export buttons in Phase 1F, auto-refresh 1G)
// - Auto-retries transient network errors (no response, 5xx) up to 2x
//   with exponential backoff; surfaces a user-friendly message on failure.

import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { setWidgetLoading } from 'app/slice/executive.slice'
import type { ExecutiveFilterState } from 'api/executive/executive.types'

interface ExecutiveRootState {
  executive: {
    filters: ExecutiveFilterState
    lastRefreshAt: number | null
  }
}

export interface ExecutiveQueryResult<R> {
  data: R | null
  loading: boolean
  error: Error | null
  refetch: () => void
}

/** True if the error is a transient network/server issue worth retrying. */
function isRetryable(err: any): boolean {
  // Axios network error (no response) — connection refused, DNS, offline, timeout
  if (err && (err.code === 'ECONNABORTED' || err.code === 'ERR_NETWORK')) return true
  if (err && !err.response) return true
  const status = err?.response?.status
  // Retry 5xx but never 4xx (those are client/permission errors)
  return typeof status === 'number' && status >= 500 && status < 600
}

/** Friendlier message from axios-style errors. */
function normalizeError(err: any): Error {
  if (err instanceof Error && !(err as any).response) return err
  const status = err?.response?.status
  const data = err?.response?.data
  const msg =
    (typeof data === 'string' ? data : data?.message) ||
    err?.message ||
    'Unknown error'
  const finalMsg = status ? `[${status}] ${msg}` : msg
  const out = new Error(finalMsg)
  ;(out as any).status = status
  ;(out as any).original = err
  return out
}

const RETRY_DELAYS_MS = [400, 1200] // up to 2 retries, exp-ish backoff
const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, ms))

// Coalesce rapid filter changes into one fetch. Without this, typing into
// the custom-date input (or any other rapid sequence of dispatches) would
// fire one request per character × every widget on the screen.
//
// Manual refetches (the `markRefreshed` tick) skip the debounce — the user
// pressed Refresh, they want it now.
const FILTER_DEBOUNCE_MS = 220

export interface UseExecutiveQueryOptions {
  // Drill-down pre-filter overrides. Merged over Redux filters before
  // buildParams runs. Serialised into the effect deps via JSON.stringify
  // so changes trigger a refetch.
  scopedFilters?: Partial<ExecutiveFilterState>
  // Extra cache-busting key for buildParams closures that depend on
  // widget-local state (e.g. an "exclude orthodontics" toggle). Any
  // serialisable value works — when it changes, the query re-runs.
  extraKey?: unknown
}

export function useExecutiveQuery<P, R>(
  widgetKey: string,
  queryFn: (params: P) => Promise<R>,
  buildParams: (filters: ExecutiveFilterState) => P | null,
  options: UseExecutiveQueryOptions = {}
): ExecutiveQueryResult<R> {
  const dispatch = useDispatch()
  const filters = useSelector((s: ExecutiveRootState) => s.executive.filters)
  const lastRefreshAt = useSelector((s: ExecutiveRootState) => s.executive.lastRefreshAt)
  const scopedFilters = options.scopedFilters
  const scopedKey = useMemo(() => JSON.stringify(scopedFilters ?? null), [scopedFilters])
  const extraKey = useMemo(() => JSON.stringify(options.extraKey ?? null), [options.extraKey])

  const [data, setData] = useState<R | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const reqId = useRef(0)

  const run = useCallback(async () => {
    const id = ++reqId.current
    const merged = scopedFilters ? { ...filters, ...scopedFilters } : filters
    const params = buildParams(merged)
    if (params == null) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)

    let attempt = 0
    // 1 initial + RETRY_DELAYS_MS.length retries
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        const res = await queryFn(params)
        if (id !== reqId.current) return
        setData(res)
        setLoading(false)
        return
      } catch (e) {
        if (id !== reqId.current) return
        if (attempt < RETRY_DELAYS_MS.length && isRetryable(e)) {
          await sleep(RETRY_DELAYS_MS[attempt++])
          if (id !== reqId.current) return
          continue
        }
        setError(normalizeError(e))
        setLoading(false)
        return
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, lastRefreshAt, scopedKey, extraKey])

  // Debounce filter-driven refetches; refresh-button refetches fire
  // immediately. The first run on mount also fires immediately (no debounce
  // for initial load).
  const firstRunRef = useRef(true)
  const lastRefreshSeenRef = useRef<number | null>(null)
  useEffect(() => {
    const isManualRefresh = lastRefreshAt !== lastRefreshSeenRef.current
    lastRefreshSeenRef.current = lastRefreshAt
    if (firstRunRef.current || isManualRefresh) {
      firstRunRef.current = false
      run()
      return
    }
    const handle = window.setTimeout(run, FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(handle)
  }, [run, lastRefreshAt])

  // Register loading state in Redux so export buttons can gate.
  useEffect(() => {
    dispatch(setWidgetLoading({ key: widgetKey, loading }))
  }, [widgetKey, loading, dispatch])

  // Cleanup registration on unmount (loading=false deletes the entry).
  useEffect(() => {
    return () => {
      dispatch(setWidgetLoading({ key: widgetKey, loading: false }))
    }
  }, [widgetKey, dispatch])

  return { data, loading, error, refetch: run }
}
